// ===== Shared Convert/Download UI pieces =====
// The Convert tab (local files) and the Download tab (saved canvases) feed the
// same converter and render the same result cards, so the network picker, the
// card markup and the card wiring live here instead of in both tabs.

import { escapeHtml, fmtSize, downloadBlob, safeFileName, pickSaveFile, writeFile, saveBlobAs, toast } from './util.js';
import { NETWORKS, makeZip, stripNetworkSuffix } from './convert-core.js';

export const CONVERT_ICON = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg>`;

// Network checkbox row. `selected` is a Set the caller owns — this mutates it
// in place and calls onChange() so the caller can refresh its own hint/button.
export function renderNetworkPicker(el, selected, onChange) {
  if (!el) return;
  el.innerHTML = NETWORKS.map(n => `
    <label class="cv-net ${selected.has(n.key) ? 'on' : ''}" data-key="${n.key}">
      <input type="checkbox" ${selected.has(n.key) ? 'checked' : ''}>
      <span class="cv-net-dot ${n.key}"></span>
      <span>${escapeHtml(n.name)}</span>
    </label>`).join('');
  el.querySelectorAll('.cv-net').forEach(label => {
    label.querySelector('input').addEventListener('change', (e) => {
      if (e.target.checked) selected.add(label.dataset.key);
      else selected.delete(label.dataset.key);
      label.classList.toggle('on', e.target.checked);
      onChange();
    });
  });
}

// One result card. `r` is { ok, network, inputName, htmlName, size, log, … }.
export function renderCard(r, idx) {
  if (!r.ok) {
    return `<div class="cv-card err">
      <div class="cv-card-top"><span class="badge ${r.network.key}">${escapeHtml(r.network.name)}</span><span class="cv-card-name">${escapeHtml(r.inputName)}</span></div>
      <div class="cv-card-error">⚠ ${escapeHtml(r.error)}</div></div>`;
  }
  const logRows = r.log.map(l => `<div class="cv-log-row ${l.ok ? '' : 'warn'}"><span class="cv-log-step">[${l.step}]</span> ${escapeHtml(l.label)}</div>`).join('');
  return `<div class="cv-card">
    <div class="cv-card-top">
      <span class="badge ${r.network.key}">${escapeHtml(r.network.name)}</span>
      <span class="cv-card-name">${escapeHtml(r.htmlName)}</span>
      <span class="cv-card-size">${fmtSize(r.size)}</span>
    </div>
    <div class="cv-card-actions">
      <button data-dl="html" data-idx="${idx}">HTML</button>
      <button data-dl="zip" data-idx="${idx}">ZIP</button>
      <button class="cv-ghost" data-toggle-log>변환 로그</button>
    </div>
    <div class="cv-card-log">${logRows}</div></div>`;
}

// Download buttons + log toggles for every card inside `el`.
export function wireResultCards(el, results) {
  el.querySelectorAll('[data-dl]').forEach(b => {
    b.addEventListener('click', () => {
      const r = results[+b.dataset.idx];
      // No await before the picker call — it needs the click's user gesture.
      if (b.dataset.dl === 'html') saveBlobAs(r.htmlBlob, r.htmlName);
      else saveBlobAs(r.zipBlob, r.zipName);
    });
  });
  el.querySelectorAll('[data-toggle-log]').forEach(b => {
    b.addEventListener('click', () => b.closest('.cv-card').querySelector('.cv-card-log').classList.toggle('open'));
  });
}

// Name for the bundle. One run over one source shares a stem, so use it;
// several sources have no single right name, so fall back to a count.
function bundleName(okResults) {
  const stems = new Set(okResults.map(r =>
    stripNetworkSuffix(r.htmlName.replace(/\.html?$/i, ''))));
  return stems.size === 1
    ? `${[...stems][0]}.zip`
    : `converted_${stems.size}_items.zip`;
}

// Save every successful result as one ZIP, foldered by network:
//   Unity Ads/stem_UnityAds.html
//   Unity Ads/stem_UnityAds.zip
//   AppLovin/stem_AppLovin.html …
// One file means one save dialog, and nothing can go missing halfway.
export async function downloadAll(okResults) {
  if (!okResults.length) return;

  // Pick first: zipping several MB can outlast the click's user activation,
  // and the picker would then be refused.
  const name = bundleName(okResults);
  const handle = await pickSaveFile(name);
  if (handle === null) return;                  // cancelled

  const entries = [];
  for (const r of okResults) {
    const dir = safeFileName(r.network.name);
    entries.push({ name: `${dir}/${safeFileName(r.htmlName)}`, data: new Uint8Array(await r.htmlBlob.arrayBuffer()) });
    entries.push({ name: `${dir}/${safeFileName(r.zipName)}`, data: new Uint8Array(await r.zipBlob.arrayBuffer()) });
  }
  const bundle = await makeZip(entries);

  if (handle) await writeFile(handle, bundle);
  else downloadBlob(bundle, name);
  toast(`${entries.length}개 파일을 ${name}으로 저장했습니다`);
}
